<template>
  <transition mode="out-in" name="fade">
    <e-form v-if="!sentStatus" v-model="isValid" key="form" @submit="submit">
      <property-block class="mb-6">
        <template #text>
          <div class="paragraph-l font-600">Обращение</div>
        </template>
        <template #content>
          <e-input
            v-if="!isContentPage"
            v-model="data.subjectAppeal"
            :rules="[required(), maxLength(150)]"
            label="Тема обращения"
            class="mb-4"
          />
          <e-textarea
            v-model="data.description"
            label="Описание"
            :rules="[required(), maxLength(32767)]"
            :rows="isContentPage ? 9 : 8"
          />
        </template>
      </property-block>

      <property-block class="mb-6">
        <template #text>
          <div class="paragraph-l font-600">Дополнительные файлы и&nbsp;материалы</div>
        </template>
        <template #content>
          <e-file-uploader
            v-model="data.files"
            multiple
            :accept="availableFileExtensions"
            :max-size="maxFileSize"
            :max-total-size="maxTotalSize"
            :max-files-count="10"
            :rules="{ extension: availableFileExtensions, maxSize: maxFileSize }"
          />
        </template>
      </property-block>

      <property-block class="mb-6">
        <template #text>
          <div class="paragraph-l font-600">Контакты для связи</div>
        </template>
        <template #content>
          <e-input v-model="data.userName" label="Имя" class="mb-4" :rules="[required(), maxLength(25)]" />
          <div class="feedback__content-fields">
            <phone-field v-model="data.userPhone" :rules="[required()]" />
            <e-input v-model="data.userEmail" label="Email" :rules="[required(), emailRule()]" />
          </div>
        </template>
      </property-block>

      <property-block>
        <template #content>
          <div class="d-flex">
            <v-spacer />
            <e-btn variant="primary" type="submit" :loading="isLoading"> Отправить</e-btn>
          </div>
        </template>
      </property-block>
    </e-form>

    <message-sent
      v-else
      key="sent-message"
      class="feedback__message-sent"
      :sent-status="sentStatus"
      :message="message"
      @send-new-message="sendNewMessage"
    />
  </transition>
</template>

<script lang="ts" setup>
import { reactive, ref, toRef } from 'vue';
import type { LoadedFile } from '@/core/types/files';
import PhoneField from '@/core/components/fields/PhoneField.vue';
import PropertyBlock from '@/core/components/interface/PropertyBlock.vue';
import { EBtn, EFileUploader, EForm, EInput, ETextarea } from '@/core/components/ui';
import { getConfigValue } from '@/core/consts/config';
import { fileExtensions, imageExtensions, videoExtensions } from '@/core/utils/file';
import { clearPhoneFormat } from '@/core/utils/string';
import { emailRule, maxLength, required } from '@/core/utils/validation';
import { useAuthStore } from '@/parts/auth/store/auth';
import MessageSent from '@/parts/support/components/MessageSent.vue';
import { useSupportStore } from '@/parts/support/store/support';

const maxFileSize = getConfigValue('maxUploadFileSize');
const availableFileExtensions = `${imageExtensions}, ${videoExtensions}, ${fileExtensions}`;
const maxTotalSize = 25;

const props = defineProps<{
  isContentPage?: boolean;
  accomodationObjectCid?: string;
}>();

const { sendToSupport, sendContentFeedback } = useSupportStore();

const { profile } = useAuthStore();

const data = reactive(getInitialData());

function getInitialData() {
  return {
    subjectAppeal: '',
    description: '',
    userName: profile.value?.profile?.name || null,
    userPhone: profile.value?.profile?.phoneNumber || null,
    userEmail: profile.value?.email || null,
    files: [] as LoadedFile[],
  };
}

const sentStatus = ref<'success' | 'error'>();
const isValid = ref(false);

const message = toRef(
  () => `Обращение в Поддержку.\n${data.subjectAppeal ? `\nТема обращения:\n${data.subjectAppeal}\n\n` : ''}${data.description ? `Описание:\n${data.description}\n` : ''}
Контакты для связи:
Имя: ${data.userName}
Телефон: ${data.userPhone}
Email: ${data.userEmail}
Код пространства: ${profile.value?.supplierCode}`,
);

const isLoading = ref(false);

const submit = async () => {
  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const files = data.files.filter((file) => !file.error && file.file).map((f) => f.file!);

  let result;

  if (!props.isContentPage) {
    result = await sendToSupport({
      subject: data.subjectAppeal,
      description: data.description,
      userName: data.userName!,
      phoneNumber: clearPhoneFormat(data.userPhone!),
      email: data.userEmail!,
      attachments: files,
    });
  } else if (props.accomodationObjectCid) {
    result = await sendContentFeedback({
      accomodationObjectCid: props.accomodationObjectCid,
      message: data.description,
      userName: data.userName!,
      phoneNumber: clearPhoneFormat(data.userPhone!),
      email: data.userEmail!,
      attachments: files,
    });
  }

  isLoading.value = false;

  if (!result || result.errors) {
    sentStatus.value = 'error';
  } else {
    sentStatus.value = 'success';
    resetForm();
  }
};

const sendNewMessage = () => {
  sentStatus.value = undefined;
};

const resetForm = () => {
  Object.assign(data, getInitialData());
};
</script>

<style lang="scss" scoped>
.feedback {
  &__content {
    &-fields {
      display: flex;

      gap: 16px;

      @include xs {
        flex-direction: column;
      }
    }
  }

  &__message-sent {
    max-width: 640px;
    margin: auto;
  }
}
</style>
